import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { initialState } from 'store/practicas/reducer';
import {
  AlumnoAuditoria,
  AlumnoPractica,
  CursoAuditoria,
  CursoPractica,
  FormadorCurso,
  Estacion,
  EstadoCurso,
  EstadoPractica,
  FechaMaximaPractica,
  FechaMinimaPractica,
  FiltroPractica,
  InfoUsers,
  InstructorData,
  InstructorPractica,
  JornadaUsuario,
  Practica,
  PracticaAuditoria,
  PracticasCount,
  PracticaSeleccionada,
  Provincias,
  Sociedad,
  State,
  TipoPractica,
  TipoServicio,
} from 'store/practicas/types';
import { RequestStatus } from 'types';
import { makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected } from 'store/usuarios/selectors';
import {
  parseDate,
  ParsePractices,
  parsePracticeStatus,
  parseTime,
} from '../../utils/parsePractices';
import { RouteTypes, SortingSort } from '../../consts';
import { DatosPerfilesUsuario } from 'store/usuarios/types';
import { getCurrentListNameToCount } from './utils';
import { getObservations } from 'utils/practiceUtils';

const selectState = (state: RootState) => state.practicas || initialState;

// Directly reducer
export const makeSelectCursosPractica = () =>
  createSelector(
    selectState,
    (state: State): CursoPractica[] => state.cursosPractica
  );


export const makeSelectFormadoresCursos = () =>
  createSelector(
    selectState,
    (state: State): FormadorCurso[] => state.formadoresCursos
  );  

export const makeSelectCursosAuditoria = () =>
  createSelector(
    selectState,
    (state: State): CursoAuditoria[] => state.cursoAuditoria
  );

export const makeSelectSelectedCursosAuditoria = () =>
  createSelector(
    selectState,
    (state: State): number[] => state.selectedCursosAuditoria
  );

export const makeSelectAlumnosAuditoria = () =>
  createSelector(
    selectState,
    (state: State): AlumnoAuditoria[] => state.alumnosAuditoria
  );

export const makeSelectAlumnosAuditoriaParse = () =>
  createSelector(
    makeSelectAlumnosAuditoria(),
    (alumnosAuditoria: AlumnoAuditoria[]): string =>
      JSON.stringify(
        alumnosAuditoria.map((e: AlumnoAuditoria) => ({
          label: e.ID_ALUMNO + ' - ' + e.NOMBRE_ALUMNO,
          value: e,
        }))
      )
  );

export const makeSelectCursosAuditoriaParse = () =>
  createSelector(
    makeSelectCursosAuditoria(),
    (cursosAuditoria: CursoAuditoria[]): string =>
      JSON.stringify(
        cursosAuditoria.map((e: CursoAuditoria) => ({
          label: e.ID_CURSO + ' - ' + e.NOMBRE_CURSO,
          value: e,
        }))
      )
  );

export const makeSelectProvincias = () =>
  createSelector(selectState, (state: State): Provincias[] => state.provincias);

export const makeSelectProvinciasParse = () =>
  createSelector(makeSelectProvincias(), (provincias: Provincias[]): string =>
    JSON.stringify(
      provincias.map((provincia: Provincias) => ({
        label: provincia,
        value: provincia,
        active: false,
      }))
    )
  );

export const makeSelectSelectedProvinciasAuditoria = () => {
  return createSelector(selectState, (state: State): any => {
    return state.selectedProvinciaAuditoria;
  });
};

export const makeSelectSelectedSociedadAuditoria = () =>
  createSelector(
    selectState,
    (state: State): string[] => state.selectedSociedadAuditoria
  );

export const makeSelectProvinciasAuditoria = () => {
  return createSelector(
    selectState,
    (state: State): any => state.provinciasAuditoria
  );
};

export const makeSelectProvinciasAuditoriaParse = () =>
  createSelector(
    makeSelectProvinciasAuditoria(),
    makeSelectSelectedProvinciasAuditoria(),
    (provincias: string[], selectedProvincia): string => {
      return JSON.stringify(
        provincias.map((provincia: string) => ({
          label: provincia,
          value: provincia,
          /* active: Boolean(provincias.find(provincia => provincia === selectedProvincia.value)).toString(), */
          active: 'false',
        }))
      );
    }
  );

export const makeSelectCursosPracticaParse = () =>
  createSelector(
    makeSelectCursosPractica(),
    makeSelectPracticaSeleccionada(),
    (
      cursoPractica: CursoPractica[],
      practicaSeleccionada: PracticaSeleccionada
    ): string =>
      JSON.stringify(
        cursoPractica.map((e: CursoPractica) => ({
          label: e?.ID_CURSO + ' - ' + e.NOMBRE_CURSO,
          value: e,
          active:
            practicaSeleccionada?.CABECERA_FICHA?.ID_CURSO ===
              e?.ID_CURSO.toString()
              ? 'true'
              : 'false',
        }))
      )
  );

export const makeSelectCursosPracticaParse2 = () =>
  createSelector(
    makeSelectCursosPractica(),
    makeSelectPracticaSeleccionada(),
    (
      cursoPractica: CursoPractica[],
      practicaSeleccionada: PracticaSeleccionada
    ): string =>
      JSON.stringify(
        cursoPractica.map((e: CursoPractica) => ({
          label: e?.ID_CURSO + ' - ' + e.NOMBRE_CURSO,
          value: e.ID_CURSO,
          active:
            practicaSeleccionada?.CABECERA_FICHA?.ID_CURSO ===
              e?.ID_CURSO.toString()
              ? 'true'
              : 'false',
          state: e.ID_ESTADO,
        }))
      )
  );

export const makeSelectAlumnosPractica = () =>
  createSelector(
    selectState,
    (state: State): AlumnoPractica[] => state.alumnosPractica
  );

export const makeSelectAlumnoPracticaParse = () =>
  createSelector(
    makeSelectAlumnosPractica(),
    (alumnoPractica: AlumnoPractica[]): string =>
      JSON.stringify(
        alumnoPractica.map((e: AlumnoPractica) => ({
          label: e.ID_ALUMNO + ' - ' + e.NOMBRE_ALUMNO,
          value: e,
        }))
      )
  );

export const makeSelectAlumnoPracticaParse2 = () =>
  createSelector(
    makeSelectAlumnosPractica(),
    (alumnoPractica: AlumnoPractica[]): string =>
      JSON.stringify(
        alumnoPractica.map((e: AlumnoPractica) => ({
          label: e.ID_ALUMNO + ' - ' + e.NOMBRE_ALUMNO,
          value: e.ID_ALUMNO,
        }))
      )
  );

export const makeSelectEstadosCursos = () =>
  createSelector(
    selectState,
    (state: State): EstadoCurso[] => state.estadosCursos
  );

export const makeSelectFechaMinimaPractica = () =>
  createSelector(
    selectState,
    (state: State): FechaMinimaPractica => state.fechaMinimaPractica
  );

export const makeSelectFechaMaximaPractica = () =>
  createSelector(
    selectState,
    (state: State): FechaMaximaPractica => state.fechaMaximaPractica
  );

export const makeSelectSorting = () =>
  createSelector(selectState, (state: State): string | '' => state.orden);
export const makeSelectSortingSort = () =>
  createSelector(
    selectState,
    (state: State): SortingSort | '' => state.ascDesc
  );

export const makeSelectPracticaSeleccionada = () =>
  createSelector(
    selectState,
    (state: State): PracticaSeleccionada => state.practicaSeleccionada
  );

export const makeSelectPracticasByJornada = () =>
  createSelector(
    selectState,
    (state: State): Practica[] => state.practicasByJornadaList
  );

export const makeSelectCursosActivoJornada = () =>
  createSelector(
    selectState,
    (state: State): CursoPractica[] => state.cursosActivosJornada
  );

export const makeSelectInstructoresPracticas = () =>
  createSelector(
    selectState,
    (state: State): InstructorPractica[] => state.instructoresPracticas
  );


export const makeSelectInstructoresPracticasParse2 = () =>
  createSelector(
    makeSelectInstructoresPracticas(),
    (instructoresPracticas: InstructorPractica[]): string =>
      JSON.stringify(
        instructoresPracticas.map((e: InstructorPractica) => ({
          label: e.ID_USUARIO + ' - ' + e.NOMBRE_USUARIO,
          value: e.ID_USUARIO,
          active: false,
        }))
      )
  );

//filIns
export const makeSelectInstructores = () =>
  createSelector(
    selectState,
    (state: State): InstructorData[] => state.instructorData
  );
export const makeSelectInstructores2 = () =>
  createSelector(
    makeSelectInstructores(),
    (instructoresPracticas: InstructorData[]): string =>
      JSON.stringify(
        instructoresPracticas.map((e: InstructorData) => ({
          label: e.ID_INSTRUCTOR + ' - ' + e.NOMBRE_INSTRUCTOR,
          value: e.ID_INSTRUCTOR,
          active: false,
        }))
      )
  );

//Combinacion InstructoresPracticasParse2 y instructores
export const makeSelectInstructoresCombinadoParse = () =>
  createSelector(
    selectState,
    (state: State): string => {
      const filtrados = state.instructorData;
      const generales = state.instructoresPracticas;

      const fuente = filtrados && filtrados.length > 0 ? filtrados : generales;

      const opciones = fuente.map((e: any) => {
        const id = e.ID_INSTRUCTOR || e.ID_USUARIO;
        const nombre = e.NOMBRE_INSTRUCTOR || e.NOMBRE_USUARIO;

        return {
          label: `${id} - ${nombre}`,
          value: id,
          active: false,
        };
      });

      return JSON.stringify(opciones);
    }
  );

export const makeSelectPracticasCount = () =>
  createSelector(
    selectState,
    (state: State): PracticasCount => state.practicasCount
  );

export const makeSelectPracticasAuditoriaCount = () =>
  createSelector(
    selectState,
    (state: State): PracticasCount => state.practicasAuditoriaCount
  );

export const makeSelectListCount = () =>
  createSelector(
    selectState,
    (state: State): PracticasCount => state[getCurrentListNameToCount()]
  );

export const makeSelectPracticas = () =>
  createSelector(selectState, (state: State): Practica[] => state.practicas);

export const makeSelectList = () =>
  createSelector(
    selectState,
    (state: State): Practica[] | PracticaAuditoria[] => {
      switch (window.location.pathname) {
        case `${RouteTypes.AUDIT}`:
          return state.practicasAuditoria;
        case `${RouteTypes.HOME}`:
          return state.practicas;
        default:
          return [];
      }
    }
  );

export const makeSelectTodasPracticasAuditoria = () =>
  createSelector(
    selectState,
    (state: State): PracticaAuditoria[] => state.todasPracticasAuditoria
  );

export const makeSelectPages = () =>
  createSelector(selectState, (state: State): number => state.pages);

export const makeSelectSelectedPage = () =>
  createSelector(selectState, (state: State): number => state.selectedPage);
export const makeSelectElementsPerPage = () =>
  createSelector(selectState, (state: State): number => state.elementsPerPage);

export const makeSelectPracticasAuditoria = () =>
  createSelector(
    selectState,
    (state: State): PracticaAuditoria[] => state.practicasAuditoria
  );

export const makeSelectInfoUser = () =>
  createSelector(selectState, (state: State): InfoUsers[] => state.infoUser);

export const makeSelectPracticasAuditoriaParse = () =>
  createSelector(
    makeSelectPracticasAuditoria(),
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    makeSelectInfoUser(),
    (
      practicaAuditoria: PracticaAuditoria[],
      profileActive: DatosPerfilesUsuario,
      infoUser: InfoUsers[]
    ): string =>
      JSON.stringify(
        practicaAuditoria.map((practica: PracticaAuditoria, index: any) => ({
          table: [
            [
              {
                text: practica.ID_CURSO,
              },
              {
                text:
                  parseDate(practica.FECHA_INI_SERVICIO) +
                  ' ' +
                  parseTime(practica.HORA_INI_SERVICIO),
              },
              {
                text:
                  parseDate(practica.FECHA_FIN_SERVICIO) +
                  ' ' +
                  parseTime(practica.HORA_FIN_SERVICIO),
              },
              {
                text: practica.MIN_PRACTICA,
              },
              {
                text: practica.DESC_PRACTICA,
              },
              {
                tooltip: [
                  {
                    title: 'Estación Inicio: ',
                  },
                  {
                    paragraph:
                      practica.COD_ESTACION_INI +
                      ' - ' +
                      practica.DESC_ESTACION_INI,
                  },
                  {
                    title: 'Estación Fin: ',
                  },
                  {
                    paragraph:
                      +practica.COD_ESTACION_FIN +
                      ' - ' +
                      practica.DESC_ESTACION_FIN,
                  },
                ],
              },
              {
                text: practica.NUM_TREN ? practica.NUM_TREN : ' - ',
              },
              {
                tooltip: [
                  {
                    title: 'Fecha-hora Inicio / Fin: ',
                  },
                  {
                    paragraph: practica.jornadaRelacionada
                      ? parseDate(
                        practica.jornadaRelacionada[0].FECHA_INI_JORNADA
                      ) +
                      ' - ' +
                      parseTime(
                        practica.jornadaRelacionada[0].HORA_INI_JORNADA
                      ) +
                      ' / ' +
                      parseDate(
                        practica.jornadaRelacionada[0].FECHA_FIN_JORNADA
                      ) +
                      ' - ' +
                      parseTime(
                        practica.jornadaRelacionada[0].HORA_FIN_JORNADA
                      )
                      : '',
                  },
                  {
                    title: 'Observaciones alumno / instructor: ',
                  },
                  {
                    paragraph: getObservations(practica),
                  },
                  {
                    title: 'Minutos puesta a disposición: ',
                  },
                  {
                    paragraph:
                      practica.MIN_PUESTA_DISP +
                      ' minutos puesta a disposición',
                  },
                ],
              },
              {
                text: infoUser[index]
                  ? practica.ID_ALUMNO + ' - ' + infoUser[index].NOMBRE_USUARIO
                  : ' '
              },
              {
                text:
                  practica.ID_INSTRUCTOR ? practica.ID_INSTRUCTOR + ' - ' + practica.NOMBRE_INSTRUCTOR : ' '
              },
              {
                text: parsePracticeStatus(practica.ID_EST_PRACTICA),
              },
              {
                checkRow: 'false',
                label: practica.ID_PRACTICA,
                data: practica.ID_PRACTICA,
              },
              {
                urlIcon: RouteTypes.DETAIL.replace(
                  ':id',
                  String(practica?.ID_PRACTICA)
                ),
                ariaLabelIcon: 'Abrir modal',
                label: practica.ID_PRACTICA,
              },
            ],
          ],
        }))
      )
  );

export const makeSelectPracticesParse = () =>
  createSelector(
    makeSelectPracticas(),
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    makeSelectInfoUser(),
    (
      practicas: Practica[],
      datosPerfilUsuarioSelected: DatosPerfilesUsuario,
      infoUsers: InfoUsers[]
    ): string =>
      JSON.stringify(
        ParsePractices(
          practicas,
          datosPerfilUsuarioSelected?.ID_PERFIL,
          infoUsers
        )
      )
  );

export const makeSelectEstadosPracticas = () =>
  createSelector(
    selectState,
    (state: State): EstadoPractica[] => state.estadosPractica
  );

export const makeSelectTiposServicio = () =>
  createSelector(
    selectState,
    (state: State): TipoServicio[] => state.tiposServicio
  );

export const makeSelectTiposServicioParse = () =>
  createSelector(
    makeSelectTiposServicio(),
    makeSelectPracticaSeleccionada(),
    (
      tiposServicio: TipoServicio[],
      practicaSeleccionada: PracticaSeleccionada
    ): string =>
      JSON.stringify(
        tiposServicio.map((e: TipoServicio) => ({
          label: e?.ID_SERVICIO + ' - ' + e.DESC_SERVICIO,
          value: e,
          active:
            practicaSeleccionada?.CUERPO_FICHA?.ID_SERVICIO === e?.ID_SERVICIO
              ? 'true'
              : 'false',
        }))
      )
  );

export const makeSelectSociedades = () =>
  createSelector(selectState, (state: State): Sociedad[] => state.sociedades);

export const makeSelectSociedadesParse = () =>
  createSelector(makeSelectSociedades(), (sociedades: Sociedad[]): string =>
    JSON.stringify(
      sociedades.map((e: Sociedad) => ({
        label: e.ID_SOCIEDAD + ' - ' + e.NOMBRE_SOCIEDAD,
        value: e,
      }))
    )
  );

// Remake practicas
export const makeSelectSociedadesParse2 = () =>
  createSelector(makeSelectSociedades(), (sociedades: Sociedad[]): string =>
    JSON.stringify(
      sociedades.map((e: Sociedad) => ({
        label: e.ID_SOCIEDAD + ' - ' + e.NOMBRE_SOCIEDAD,
        value: e.ID_SOCIEDAD,
      }))
    )
  );

export const makeSelectJornadasUsuario = () =>
  createSelector(
    selectState,
    (state: State): JornadaUsuario[] => state.jornadasUsuario
  );

export const makeSelectEstaciones = () =>
  createSelector(selectState, (state: State): Estacion[] => state.estaciones);


export const makeSelectTiposPractica = () =>
  createSelector(
    selectState,
    (state: State): TipoPractica[] => state.tiposPractica
  );

export const makeSelectTiposPracticaParse = () =>
  createSelector(
    makeSelectTiposPractica(),
    makeSelectPracticaSeleccionada(),
    (
      tipoPractica: TipoPractica[],
      practicaSeleccionada: PracticaSeleccionada
    ): string =>
      JSON.stringify(
        tipoPractica.map((e: TipoPractica) => ({
          label: e?.ID_TIPO_PRACTICA + ' - ' + e.DESC_PRACTICA,
          value: e,
          active:
            practicaSeleccionada?.CUERPO_FICHA?.ID_TIPO_PRACTICA ===
              e?.ID_TIPO_PRACTICA
              ? 'true'
              : 'false',
        }))
      )
  );

export const makeSelectGetActivatedJourneyCourses = () =>
  makeSelectCursosActivoJornada();

// Combined
export const makeSelectEstadosPracticasParse = () =>
  createSelector(
    makeSelectEstadosPracticas(),
    (estadosPractica: EstadoPractica[]): string =>
      JSON.stringify(
        estadosPractica.map((estadoPractica: EstadoPractica) => ({
          label: estadoPractica.NOMBRE_EST_PRACTICA,
          value: estadoPractica.ID_EST_PRACTICA,
        }))
      )
  );

// Requests statuses
export const makeSelectPostAnularPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.postAnularPracticaRequestStatus
  );

export const makeSelectPostRechazarPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.postRechazarPracticaRequestStatus
  );

export const makeSelectPostReclamarPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.postReclamarPracticaRequestStatus
  );

export const makeSelectPostValidarPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.postValidarPracticaRequestStatus
  );

export const makeSelectGetFilterCursoPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getFilterCursoPracticaRequestStatus
  );

export const makeSelectGetFilterAlumnoPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getFilterAlumnoPracticaRequestStatus
  );

export const makeSelectGetEstadosCursosRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getEstadosCursosRequestStatus
  );

export const makeSelectGetFechaMinimaPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getFechaMinimaPracticaRequestStatus
  );

export const makeSelectGetFechaMaximaPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getFechaMaximaPracticaRequestStatus
  );

export const makeSelectGetPracticaSeleccionadaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getPracticaSeleccionadaRequestStatus
  );

export const makeSelectGetCursosActivosJornadaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getCursosActivosJornadaRequestStatus
  );

export const makeSelectGetInstructoresPracticasRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getInstructoresPracticasRequestStatus
  );

export const makeSelectCrearPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.crearPracticaRequestStatus
  );

export const makeSelectGetPracticasByJornadaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getPracticasByJornadaRequestStatus
  );

export const makeSelectGetPracticasCountRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getPracticasCountRequestStatus
  );

export const makeSelectGetPracticasRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getPracticasRequestStatus
  );
export const makeSelectGetAuditPracticesRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getPracticasAuditoriaRequestStatus
  );

export const makeSelectGetEstadosPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getEstadosPracticaRequestStatus
  );

export const makeSelectGetTipoServicioRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getTipoServicioRequestStatus
  );

export const makeSelectGetSociedadesRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getSociedadesRequestStatus
  );

export const makeSelectGetJornadasUsuarioRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getJornadasUsuarioRequestStatus
  );

export const makeSelectGetEstacionesRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getEstacionesRequestStatus
  );

export const makeSelectGetTiposPracticaRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getTiposPracticaRequestStatus
  );

export const makeSelectPracticasList = () =>
  createSelector(
    selectState,
    (state: State): Practica[] => state.practicasList
  );

export const makeSelectPracticasListCount = () =>
  createSelector(
    selectState,
    (state: State): number => state.practicasListCount
  );

// Filtros
export const makeSelectFiltros = () =>
  createSelector(
    selectState,
    (state: State): FiltroPractica[] => state.filtros
  );

export const makesSelectFiltrosParse = () =>
  createSelector(
    makeSelectFiltros(),
    (filtroPractica: FiltroPractica[]): string =>
      JSON.stringify(
        filtroPractica.map((filtro: FiltroPractica) => ({
          label: filtro.ID_FILTRO + ' - ' + filtro.NOMBRE_FILTRO,
          value: filtro.FILTRO_JSON,
        }))
      )
  );

const getInstructorLabel = (
  id: string,
  instructoresPractica: InstructorPractica[]
): string => {
  const instructor = instructoresPractica.find(
    (item) =>
      item.ID_USUARIO.toString() === id ||
      'L' + item.ID_USUARIO.toString() === id
  );

  return instructor
    ? instructor.ID_USUARIO + ' - ' + instructor.NOMBRE_USUARIO
    : id + ' - ' + 'Nombre no encontrado';
};

const parseInstructorFilter = (
  e: FiltroPractica,
  instructoresPractica: InstructorPractica[]
) => {
  const filtroData = JSON.parse(e.FILTRO_JSON);
  const instructoresIds = filtroData.instructores.split(',');

  const instructoresLabels = instructoresIds.map((id: string) =>
    getInstructorLabel(id, instructoresPractica)
  );

  return {
    label: instructoresLabels.join(', '),
    value: filtroData.instructores,
    active: false,
  };
};

const hasInstructoresFilter = (e: FiltroPractica): boolean => {
  const filtroData = JSON.parse(e.FILTRO_JSON);
  return Boolean(filtroData.instructores);
};

export const makeSelectInstructoresFiltro = () =>
  createSelector(
    makeSelectInstructoresPracticas(),
    makeSelectFiltros(),
    (
      instructoresPractica: InstructorPractica[],
      filtroPractica: FiltroPractica[]
    ): string =>
      JSON.stringify(
        filtroPractica
          .filter(hasInstructoresFilter)
          .map((e: FiltroPractica) =>
            parseInstructorFilter(e, instructoresPractica)
          )
      )
  );


const getAlumnoLabel = (
  id: string,
  alumnosPractica: AlumnoPractica[]
): string => {
  const alumno = alumnosPractica.find(
    (item) => item.ID_ALUMNO.toString() === id
  );

  return alumno
    ? alumno.ID_ALUMNO + ' - ' + alumno.NOMBRE_ALUMNO
    : id + ' - ' + 'Nombre no encontrado';
};

const parseAlumnoFilter = (
  e: FiltroPractica,
  alumnosPractica: AlumnoPractica[]
) => {
  const filtroData = JSON.parse(e.FILTRO_JSON);

  if (!filtroData.alumnos) {
    return {
      label: 'Sin alumnos',
      value: '',
    };
  }

  const alumnosIds = filtroData.alumnos.split(',');

  const alumnosLabels = alumnosIds.map((id: string) =>
    getAlumnoLabel(id, alumnosPractica)
  );

  return {
    label: alumnosLabels.join(', '),
    value: filtroData.alumnos,
  };
};

export const makeSelectAlumnosFiltro = () =>
  createSelector(
    makeSelectAlumnosPractica(),
    makeSelectFiltros(),
    (
      alumnosPractica: AlumnoPractica[],
      filtroPractica: FiltroPractica[]
    ): string =>
      JSON.stringify(
        filtroPractica.map((e: FiltroPractica) =>
          parseAlumnoFilter(e, alumnosPractica)
        )
      )
  );

const getEstadoFiltroOption = (
  estadoId: string,
  estadosPractica: EstadoPractica[]
) => {
  const estado = estadosPractica.find(
    (item) => item.ID_EST_PRACTICA === estadoId
  );

  return estado
    ? {
        label: estado.NOMBRE_EST_PRACTICA,
        value: estado.ID_EST_PRACTICA,
      }
    : {
        label: estadoId,
        value: estadoId,
      };
};

const parseEstadosFiltro = (
  e: FiltroPractica,
  estadosPractica: EstadoPractica[]
) => {
  let filtroData;

  try {
    filtroData = JSON.parse(e.FILTRO_JSON);
  } catch (error) {
    console.error(error);
    return [];
  }

  if (!filtroData?.estados) {
    return [];
  }

  return filtroData.estados
    .split(',')
    .map((estadoId: string) =>
      getEstadoFiltroOption(estadoId, estadosPractica)
    );
};

export const makeSelectEstadosFiltro = () =>
  createSelector(
    makeSelectFiltros(),
    makeSelectEstadosPracticas(),
    (
      filtroPractica: FiltroPractica[],
      estadosPractica: EstadoPractica[]
    ): string =>
      JSON.stringify(
        filtroPractica
          .map((e: FiltroPractica) =>
            parseEstadosFiltro(e, estadosPractica)
          )
          .flat()
      )
  );